import React from 'react';
import { X, CheckCircle, Mail, CreditCard, Settings, FileText, DollarSign, Clock } from 'lucide-react';

interface ServiceImplementationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ServiceImplementationModal({ isOpen, onClose }: ServiceImplementationModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Service Implementation Process</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <p className="text-blue-900">
              Setup is designed to be self-serve first, with Scale Small AI checking the finished setup before services start.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-white text-blue-600 rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                  1
                </div>
                <h3 className="text-xl font-bold">Phase 1: Tell us what to prepare</h3>
              </div>
              <p className="text-blue-50 mb-4">
                Share the basics, choose services, and review what each service will need. This phase happens now.
              </p>
            </div>

            <div className="pl-8 space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 rounded-lg p-3 mt-1">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Account Creation & Business Information</h4>
                  <p className="text-gray-600 text-sm">
                    Provide the business and contact details we use to create your dashboard login and save your setup.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-blue-100 rounded-lg p-3 mt-1">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Service & Tier Selection</h4>
                  <p className="text-gray-600 text-sm">
                    Choose the services that align with your business goals and select the tier that fits your needs.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-blue-100 rounded-lg p-3 mt-1">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Total Costs Outline</h4>
                  <p className="text-gray-600 text-sm">
                    Review a detailed breakdown of all costs associated with your selected services and tier. No payment is taken in onboarding.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-blue-100 rounded-lg p-3 mt-1">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Integration Requirements</h4>
                  <p className="text-gray-600 text-sm">
                    Review the platforms and systems your selected services may use. You will connect the ones you want next.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-100 rounded-lg p-3 mt-1">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Phase 1 Completion</h4>
                  <p className="text-gray-600 text-sm">
                    Once you submit, your login is created and sent to you with a link to Connect.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6 space-y-6">
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-white text-green-600 rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                  2
                </div>
                <h3 className="text-xl font-bold">Phase 2: Connect, confirm, and launch</h3>
              </div>
              <p className="text-green-50 mb-4">
                Connect your selected platforms, finish the dashboard setup tracker, and submit for final review.
              </p>
            </div>

            <div className="pl-8 space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-green-100 rounded-lg p-3 mt-1">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Email Notification</h4>
                  <p className="text-gray-600 text-sm">
                    You receive a secure one-time sign-in link to the Connect page in your Scale Small AI dashboard.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-100 rounded-lg p-3 mt-1">
                  <Settings className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Integration Setup</h4>
                  <p className="text-gray-600 text-sm">
                    Turn on only the platforms you want to use, then connect social accounts, photo sources, and CRM where applicable.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-100 rounded-lg p-3 mt-1">
                  <CreditCard className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Billing Setup</h4>
                  <p className="text-gray-600 text-sm">
                    After admin approval, complete Stripe billing from the dashboard tracker to start services.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-100 rounded-lg p-3 mt-1">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Admin Review & Service Start</h4>
                  <p className="text-gray-600 text-sm">
                    The system checks your setup first. Scale Small AI completes final review, then payment starts services.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> You can stop and return later. Your answers are saved as you go, and the dashboard tracker shows exactly what is left.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Got It
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
